import { useState, useCallback } from 'react';

/** The localStorage key under which `useLocalSetting(key)` stores its value. */
export const localSettingKey = (key: string): string =>
  `${import.meta.env.VITE_APP_NAME}-${key}`;

function useLocalSetting<T>(
  key: string,
  initialValue?: undefined
): [
  T | undefined,
  (
    value: T | ((oldValue: T | undefined | null) => T | undefined) | null
  ) => void,
];
function useLocalSetting<T>(
  key: string,
  initialValue: T
): [
  T,
  (
    value: T | ((oldValue: T | undefined | null) => T | undefined) | null
  ) => void,
];
function useLocalSetting<T>(
  key: string,
  initialValue?: any
): [
  any,
  (
    value: T | ((oldValue: T | undefined | null) => T | undefined) | null
  ) => void,
] {
  const namespacedKey = localSettingKey(key);
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T | undefined | null>(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(namespacedKey);
      // Parse stored json or if none return initialValue
      if (item) return JSON.parse(item) as T;
      return initialValue;
    } catch (error) {
      // If error also return initialValue
      console.error(error);
      return initialValue;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = useCallback(
    (value: T | ((oldValue: T | undefined | null) => T | undefined) | null) => {
      try {
        // Allow value to be a function so we have same API as useState
        const valueToStore =
          value instanceof Function ? value(storedValue) : value;
        // Save state
        setStoredValue(valueToStore);

        // Remove from local storage
        if (valueToStore === null) {
          window.localStorage.removeItem(namespacedKey);
        }
        // or Save to local storage
        else {
          window.localStorage.setItem(
            namespacedKey,
            JSON.stringify(valueToStore)
          );
        }
      } catch (error) {
        // A more advanced implementation would handle the error case
        console.error(error);
      }
    },
    [namespacedKey, storedValue]
  );
  return [storedValue, setValue];
}

export default useLocalSetting;
